import type { PickSampleExt } from "@novorender/api";
import { vec3 } from "gl-matrix";
import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useAddLaser } from "features/outlineLaser/hooks/useAddLaser";
import type { OutlineLaser } from "features/outlineLaser/outlineLaserSlice";
import { clippingOutlineLaserActions, selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { selectClippingPlanes } from "features/render";
import { selectCameraType } from "features/render/renderSlice";
import { CameraType, Picker } from "features/render/types";
import { undoRedoActions } from "features/undoRedo";
import { hasMouseSupport } from "utils/misc";

import { mobileMeasureActions, PICK_TARGET_OFFSET, selectSelectedLaserId } from "./mobileMeasureSlice";
import { useMobileMeasureCursor } from "./useMobileMeasureCursor";

const SELECT_RADIUS_SQ = 70 * 70;

export function useMobileLaser() {
    const dispatch = useAppDispatch();
    const {
        state: { view },
    } = useExplorerGlobals();
    const addLaser = useAddLaser();
    const cameraType = useAppSelector(selectCameraType);
    const { planes } = useAppSelector(selectClippingPlanes);
    const outlineLasers = useAppSelector(selectOutlineLasers);
    const selectedLaserId = useAppSelector(selectSelectedLaserId);
    const isMobile = !hasMouseSupport();
    const prevLasersLenRef = useRef(outlineLasers.length);

    // Auto-select next laser when the selected one is deleted
    useEffect(() => {
        if (!isMobile) {
            return;
        }
        const deletedOne = prevLasersLenRef.current > outlineLasers.length;
        prevLasersLenRef.current = outlineLasers.length;
        if (selectedLaserId && deletedOne && !outlineLasers.some((l) => l.id === selectedLaserId)) {
            const next = outlineLasers.length > 0 ? outlineLasers[outlineLasers.length - 1].id : null;
            dispatch(mobileMeasureActions.setSelectedLaserId(next));
        }
    }, [outlineLasers, selectedLaserId, isMobile, dispatch]);

    const findNearestLaser = useCallback(
        (canvasPos: { x: number; y: number }): OutlineLaser | null => {
            if (!view || outlineLasers.length === 0) {
                return null;
            }
            const laserPositions = outlineLasers.map((l) => l.laserPosition);
            const screenPositions = view.convert.worldSpaceToScreenSpace(laserPositions);
            if (!screenPositions) {
                return null;
            }

            let bestIdx = -1;
            let bestDist = SELECT_RADIUS_SQ;

            for (let i = 0; i < screenPositions.length; i++) {
                const sp = screenPositions[i];
                if (!sp) {
                    continue;
                }
                const dx = canvasPos.x - sp[0];
                const dy = canvasPos.y - sp[1];
                const dist = dx * dx + dy * dy;
                if (dist < bestDist) {
                    bestDist = dist;
                    bestIdx = i;
                }
            }

            return bestIdx !== -1 ? outlineLasers[bestIdx] : null;
        },
        [view, outlineLasers],
    );

    const onPlace = useCallback(
        async (result: PickSampleExt) => {
            if (!view) {
                return;
            }

            let laserId: string | undefined;
            let added = false;
            let position: vec3 | undefined;
            let normal: vec3 | undefined;
            let addOptions: { laserId: string; measureZ?: boolean; attachedToPlane?: boolean; objectId?: number };

            if (cameraType === CameraType.Orthographic && planes.length > 0 && view.renderState.clipping.enabled) {
                const plane = view.renderState.clipping.planes[0];
                if (!plane) {
                    return;
                }
                normal = vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]);
                position = vec3.clone(result.position) as vec3;
                laserId = crypto.randomUUID();
                addOptions = { laserId, measureZ: false, attachedToPlane: true };
                added = await addLaser(position, normal, addOptions);
            } else if (result.objectId !== -1) {
                position = vec3.clone(result.position) as vec3;
                normal = vec3.clone(result.normal) as vec3;
                laserId = crypto.randomUUID();
                addOptions = { laserId, objectId: result.objectId };
                added = await addLaser(position, normal, addOptions);
            }

            if (laserId && added && position && normal) {
                dispatch(mobileMeasureActions.setSelectedLaserId(laserId));

                const capturedLaserId = laserId;
                const capturedPosition = position;
                const capturedNormal = normal;
                const capturedOptions = addOptions!;
                dispatch(
                    undoRedoActions.push({
                        description: "Add laser measurement",
                        undoCallback: () => {
                            dispatch(clippingOutlineLaserActions.removeLaser(capturedLaserId));
                        },
                        redoCallback: () => {
                            void addLaser(capturedPosition, capturedNormal, capturedOptions).then((reAdded) => {
                                if (reAdded) {
                                    dispatch(mobileMeasureActions.setSelectedLaserId(capturedLaserId));
                                }
                            });
                        },
                    }),
                );
            }
        },
        [view, addLaser, cameraType, planes, dispatch],
    );

    const onTapElsewhere = useCallback(
        (canvasPos: { x: number; y: number }) => {
            const nearLaser = findNearestLaser(canvasPos);
            dispatch(mobileMeasureActions.setSelectedLaserId(nearLaser?.id ?? null));

            if (nearLaser && view) {
                const screenPos = view.convert.worldSpaceToScreenSpace([nearLaser.laserPosition]);
                if (screenPos?.[0]) {
                    const dpadX = screenPos[0][0] - PICK_TARGET_OFFSET.x;
                    const dpadY = screenPos[0][1] - PICK_TARGET_OFFSET.y;
                    dispatch(mobileMeasureActions.setCursorPosition({ x: dpadX, y: dpadY }));
                    dispatch(mobileMeasureActions.setIndicatorPosition({ x: screenPos[0][0], y: screenPos[0][1] }));
                    dispatch(mobileMeasureActions.setIndicatorTransform(null));
                    dispatch(mobileMeasureActions.setCursorActive(true));
                }
            }
        },
        [findNearestLaser, dispatch, view],
    );

    const isValidPick = useCallback(
        (result: PickSampleExt | null) =>
            Boolean(
                result &&
                (result.objectId !== -1 ||
                    (cameraType === CameraType.Orthographic &&
                        planes.length > 0 &&
                        view?.renderState.clipping.enabled)),
            ),
        [cameraType, planes.length, view?.renderState.clipping.enabled],
    );

    useMobileMeasureCursor({
        picker: Picker.OutlineLaser,
        onPlace,
        onTapElsewhere,
        enabled: isMobile,
        sampleDiscRadius: 4,
        isValidPick,
        persistent: true,
    });
}
